import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useCart } from '../context/CartContext';
import { NAV_LINKS, STORE_INFO } from '../mock';

const Header = () => {
  const { count, setIsOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/shop?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* Top bar */}
      <div className="bg-[#0a0a0a] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {STORE_INFO.phone}</span>
            <span className="hidden md:flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {STORE_INFO.address}</span>
            <span className="hidden lg:flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {STORE_INFO.hours}</span>
          </div>
          <div className="hidden sm:block">
            <span className="text-[#bfff00] font-semibold">FREE SHIPPING</span> on orders over ${STORE_INFO.freeShipping}
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-10 h-10 bg-[#0a0a0a] flex items-center justify-center rounded-md">
            <span className="text-[#bfff00] font-black text-xl leading-none">F</span>
          </div>
          <div className="leading-tight">
            <div className="font-black text-[#0a0a0a] text-lg tracking-tight">FITNESS PRO</div>
            <div className="text-[10px] font-bold tracking-[0.2em] text-neutral-600 -mt-0.5">NUTRITION</div>
          </div>
        </Link>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl mx-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search supplements, brands, flavours..." className="pl-9 border-neutral-300 focus-visible:ring-[#bfff00]" />
        </form>

        <div className="flex items-center gap-2 ml-auto">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)}>
            <Menu className="w-5 h-5" />
          </Button>
          <Button variant="ghost" className="relative" onClick={() => setIsOpen(true)}>
            <ShoppingCart className="w-5 h-5" />
            <span className="hidden sm:inline ml-2 text-sm font-medium">Cart</span>
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#bfff00] text-black text-[11px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{count}</span>
            )}
          </Button>
        </div>
      </div>

      {/* Nav */}
      <nav className="hidden md:block border-t border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-1">
          {NAV_LINKS.map(link => (
            <Link key={link.path} to={link.path} className="px-4 py-3 text-sm font-semibold uppercase tracking-wide text-neutral-800 hover:text-[#0a0a0a] hover:bg-neutral-50 transition-colors relative group">
              {link.label}
              <span className="absolute left-4 right-4 bottom-1 h-0.5 bg-[#bfff00] scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
            </Link>
          ))}
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 bg-white md:hidden">
          <div className="flex items-center justify-between p-4 border-b">
            <span className="font-black">Menu</span>
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}><X className="w-5 h-5" /></Button>
          </div>
          <div className="p-4 space-y-2">
            <form onSubmit={(e) => { submitSearch(e); setMobileOpen(false); }} className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9" />
            </form>
            {NAV_LINKS.map(link => (
              <Link key={link.path} to={link.path} onClick={() => setMobileOpen(false)} className="block px-3 py-3 font-semibold border-b text-neutral-800">{link.label}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
